import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTaxCategoriesQuery } from '../../api/taxCategory/taxCategoryApiSlice';
import { formatRatePercent } from './formatRatePercent';

interface TaxCategoryLabelProps {
    taxCategoryId: string | null;
}

/**
 * The read-only twin of `TaxCategorySelect` (#1353) — the same name and rate,
 * rendered as text.
 *
 * Unassigned prints `n/a` rather than nothing. A blank cell reads as "no tax",
 * which is a claim; unassigned means the VAT breakdown is withheld until
 * someone chooses, which is a different state and the one that needs fixing.
 */
const TaxCategoryLabel = ({ taxCategoryId }: Readonly<TaxCategoryLabelProps>) => {
    const { t } = useTranslation();
    const { data } = useGetTaxCategoriesQuery();

    const category = (data?.categories ?? []).find((c) => c.id === taxCategoryId);
    if (!category) return <span className={'text-foreground/50'}>{t('common.not_applicable')}</span>;

    const name = t(`tax_category.code.${category.code}`, {
        defaultValue: category.description || category.code,
    });
    const rate = category.currentRatePercent;
    if (rate === null) return <span>{name}</span>;

    return <span>{`${name} (${formatRatePercent(rate)}%)`}</span>;
};

export default TaxCategoryLabel;
